import { Component, Input, OnInit, Output, EventEmitter } from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';
import { TableModule } from 'primeng/table';
import { ButtonModule } from 'primeng/button';
import { TooltipModule } from 'primeng/tooltip';
import * as XLSX from 'xlsx';

@Component({
  selector: 'app-table-card',
  standalone: true,
  imports: [CommonModule, TableModule, ButtonModule, TooltipModule, DatePipe],
  templateUrl: './table-card.component.html',
  styleUrls: ['./table-card.component.css']
})
export class TableCardComponent implements OnInit {

  @Input() resourceName: string = '';
  @Input() tableContent: any;
  @Output() openCanvasPage = new EventEmitter<string>();

  ngOnInit(): void {
    // This logic mutates the @Input property, which is not ideal, but it matches the request.
    // It expects `tableContent` to be an array of objects and transforms it.
    if (this.tableContent && Array.isArray(this.tableContent)) {
      try {
        const tableData: any[] = this.tableContent;
        if (tableData.length > 0) {
          this.tableContent = {
            columns: Object.keys(tableData[0]).map(key => ({
              field: key,
              header: this.camelCaseToTitleCase(key)
            })),
            data: tableData
          };
        } else {
          this.tableContent = null; // Set to null if data is an empty array
        }
      } catch (e) {
        console.error("Error parsing table JSON:", e);
        this.tableContent = null;
      }
    }
  }

  onViewDetails(rowData: any): void {
    let resourceType = this.resourceName.replace(' ', '-').toLowerCase();
    let resourceId = undefined;
    if (!resourceType) {
      if (rowData.promoPlanId) {
        resourceType = 'promo-plan';
        resourceId = rowData.promoPlanId;
      } else if (rowData.selloutId) {
        resourceType = 'sellout';
        resourceId = rowData.selloutId;
      } else if (rowData.settlementId) {
        resourceType = 'settlement';
        resourceId = rowData.settlementId;
      }
      else if (rowData.activityId) {
        resourceType = 'activity';
        resourceId = rowData.activityId;
      }
    }
    if (rowData) {
      this.openCanvasPage.emit(`${resourceType}/view/${resourceId}`);
    } else {
      console.warn('View details clicked, but details not found in row data:', rowData);
    }
  }

  exportExcel() {
    if (!this.tableContent || !this.tableContent.data) {
      console.error('No data available to export.');
      return;
    }

    // Prepare data for export: use headers as keys and format dates
    const dataToExport = this.tableContent.data.map((row: any) => {
      const newRow: any = {};
      this.tableContent.columns.forEach((col: any) => {
        let value = row[col.field];
        if (this.isDateArray(value)) {
          const date = this.arrayToDate(value);
          value = date ? new Intl.DateTimeFormat('en-GB').format(date) : '';
        }
        newRow[col.header] = value;
      });
      return newRow;
    });

    const ws: XLSX.WorkSheet = XLSX.utils.json_to_sheet(dataToExport);
    const wb: XLSX.WorkBook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Sheet1');
    XLSX.writeFile(wb, 'table_export.xlsx');
  }

  isDateArray(data: any): boolean {
    // Heuristic to check if the data is a date-like array e.g., [2022, 7, 27]
    return (
      Array.isArray(data) &&
      data.length >= 3 &&
      typeof data[0] === 'number' &&
      typeof data[1] === 'number' &&
      typeof data[2] === 'number'
    );
  }

  arrayToDate(data: any[]): Date | null {
    if (!this.isDateArray(data)) return null;
    // JS Date month is 0-indexed, so subtract 1 from the month value.
    return new Date(data[0], data[1] - 1, data[2]);
  }

  /**
   * Converts a camelCase string to a more readable Title Case format.
   * e.g., 'promoPlanId' becomes 'Promo Plan ID'.
   * @param text The camelCase string to convert.
   * @returns The converted Title Case string.
   */
  private camelCaseToTitleCase(text: string): string {
    if (!text) return '';
    const withSpaces = text.replace(/([a-z])([A-Z])/g, '$1 $2');
    let titleCase = withSpaces.charAt(0).toUpperCase() + withSpaces.slice(1);
    titleCase = titleCase.replace(/\bId\b/g, 'ID');
    if (titleCase.endsWith('ID'))
      titleCase = 'ID';
    return titleCase;
  }

}
